import { useCallback, useEffect, useRef } from 'react';
import PQueue from 'p-queue';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { FolderAsset, Scalars } from '@frameio/graphql-client/generated';
import {
  useGetAncestorFoldersQuery,
  useGetAssetForNavigationQuery,
  useGetProjectForNavigationQuery,
} from '@frameio/graphql-client/generated';
import { number, plural } from '@frameio/i18n';
import { useNotificationsOptIn } from '@frameio/notifications/hooks';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTransferErrorModalContext } from '@frameio/shared/contexts/ModalContext/ModalContext';
import { getDevFlag } from '@frameio/shared/dev-flags';
import { useTrack } from '@frameio/shared/segment/hooks';
import type { FileSystemEntry } from '@frameio/shared/transfers/types';
import { emitAnalyticsEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { ToastAlert, useToast } from '@frameio/vapor';
import { UploaderConfigs } from '../constants';
import { useGetTransferBatchEventSubscription } from '../gql/subscriptions/generated/TransferBatchEvent';
import {
  ErrorTypes,
  FileSystemStreamReader,
} from '../utils/FileSystemStreamReader';
import { generateTransferBatchMetadata } from '../utils/transferBatch';
import TEXT from './generated/TEXT/useUploadWorkerQueue';
import { useUploaderWorker } from './useUploaderWorker';

/** Idle timeout for a parked direct-to-ESM file awaiting upload URLs — see the watchdog below. */
const ESM_UPLOAD_URL_TIMEOUT_MS = 2 * 60 * 1000;

TEXT.DEFINE(
  {
    /** Generic error message when upload fails */
    errorMessage: 'Your assets could not be uploaded.',
    /** Title for folder limit exceeded error */
    folderLimitExceededTitle: 'Upload Limit Reached',
    /** Title for concurrent batch limit exceeded error */
    concurrentBatchLimitExceededTitle:
      "You've reached your concurrent upload limit",
    /** Details for concurrent batch limit exceeded error */
    concurrentBatchLimitExceededDetails: (
      maxConcurrentBatchCount: number
    ): string =>
      plural(
        maxConcurrentBatchCount,
        `Frame.io currently supports ${number(
          maxConcurrentBatchCount
        )} simultaneous uploads. Cancel an upload, or wait for some to complete.`,
        'Frame.io currently supports 1 simultaneous upload. Cancel an upload, or wait for it to complete.'
      ),
    /** Title for concurrent file limit exceeded error */
    concurrentFileLimitExceededTitle: 'Upload Queue Limit Reached',
    /** Details for concurrent file limit exceeded error */
    concurrentFileLimitExceededDetails: (
      currentFileCount: number,
      maxLimit: number
    ): string =>
      `You've selected ${plural(
        currentFileCount,
        `${number(currentFileCount)} files`,
        '1 file'
      )}, but the maximum is ${number(
        maxLimit
      )}. Please select fewer files and try again.`,
    /** Title for folder depth limit exceeded error */
    folderDepthLimitExceededTitle: `Your upload's folder structure is too deep`,
    /** Details for folder depth limit exceeded error */
    folderDepthLimitExceededDetails: (maxFolderDepth: number): string =>
      plural(
        maxFolderDepth,
        `Frame.io currently supports uploading folders nested up to ${number(
          maxFolderDepth
        )} levels deep.`,
        'Frame.io currently supports uploading folders nested up to 1 level deep.'
      ),
    /** Details for folder limit exceeded error */
    folderLimitExceededDetails: (maxFolderCount: number): string =>
      plural(
        maxFolderCount,
        `Please select ${number(
          maxFolderCount
        )} or fewer folders to upload, then try again.`,
        'Please select 1 or fewer folders to upload, then try again.'
      ),
    /** Title for file limit exceeded error */
    fileLimitExceededTitle: 'Upload Limit Reached',
    /** Details for file limit exceeded error */
    fileLimitExceededDetails: (maxFileCount: number): string =>
      plural(
        maxFileCount,
        `Please select ${number(
          maxFileCount
        )} or fewer assets to upload, then try again.`,
        'Please select 1 or fewer assets to upload, then try again.'
      ),
    /** Error message when empty folders are selected */
    emptyFoldersNotSupported: 'Empty folders are not supported via file select',
    /** Title shown when direct-to-ESM upload URLs do not arrive in time */
    esmTreeUploadTimeoutTitle: 'Upload preparation timed out',
    /** Details shown when direct-to-ESM upload URLs do not arrive in time */
    esmTreeUploadTimeoutDetails:
      'We could not finish preparing your upload. Please try again.',
  },
  { plural, number }
);

// Add non-stable apis to the type
type XDataTransferItem = {
  getAsEntry: () => FileSystemEntry;
  webkitGetAsEntry: () => FileSystemEntry;
} & DataTransferItem;

type SharedInput = {
  projectId?: Scalars['ID']['output'];
  /**
   * on Data transfer we can leverage the DataTransfer entry items
   * vs. Files. However file.type may not be available.
   */
  scanFileSystemEntries?: boolean;
};

type AssetsInput = SharedInput & {
  folderId?: FolderAsset['id'];
  uploadType: 'asset';
};

type AttachmentsInput = SharedInput & {
  assetId?: Scalars['ID']['output'];
  commentId?: Scalars['ID']['output'];
  parentId?: Scalars['ID']['output'];
  uploadType: 'attachment';
};

type QueueInput = (AssetsInput | AttachmentsInput) & { skipQueries?: boolean };

const hasEntries = (entries: File[] | FileSystemEntry[]) =>
  !!entries && !!entries.length;

function valueOrDefault(config: {
  value?: number | undefined;
  defaultValue: number;
}) {
  return config.value ?? config.defaultValue;
}

/**
 * @hack:
 *
 * Ideally, the `webkitGetAsEntry()` call here should be abstracted away
 * from this event handler. However, because of a Chrome bug [1],
 * `dataTransfer.items` can get cleared out before the job queued below
 * (as a microtask) can process it. So we're forced to access `items` in
 * this function, outside of the job, where access is guaranteed.
 *
 * [1] https://bugs.chromium.org/p/chromium/issues/detail?id=1081065
 */
const getFsEntries = (
  dataTransferItems: DataTransferItemList
): FileSystemEntry[] =>
  Array.from(dataTransferItems).map(
    (item: XDataTransferItem | DataTransferItem) => {
      const getAsEntry =
        (item as XDataTransferItem).getAsEntry || item.webkitGetAsEntry;
      return getAsEntry.call(item);
    }
  );

/**
 * Put items into the upload worker as a new job.
 */
export function useUploadWorkerQueue({
  projectId,
  scanFileSystemEntries = false,
  uploadType = 'asset',
  skipQueries = false,
  ...otherInputs
}: QueueInput) {
  const { folderId } = otherInputs as AssetsInput;
  const uploader = useUploaderWorker();
  const accountId = useActiveAccountId();
  const track = useTrack();
  const { openTransferErrorModal } = useTransferErrorModalContext();
  // The next 3 hooks have empty strings as fallback values to satisfy type requirements,
  // but are skipped if real values are not provided.
  // The fallback values are never used.
  const { data: projectData } = useGetProjectForNavigationQuery({
    variables: { projectId: projectId ?? '' },
    skip: !projectId,
  });
  const sourceFolder = useGetAssetForNavigationQuery({
    variables: { assetId: folderId ?? '' },
    skip: skipQueries || !folderId,
  })?.data?.account?.asset;

  const { data: ancestorFoldersData } = useGetAncestorFoldersQuery({
    variables: { assetId: folderId ?? '' },
    skip: skipQueries || !folderId,
  });
  const ancestorFolders = ancestorFoldersData?.account?.asset?.ancestorFolders;
  const ancestorFoldersDepth = ancestorFolders?.length;

  const { maybeShowNotificationsOptInToast } = useNotificationsOptIn();

  const { addToast } = useToast();

  const project = projectData?.account?.project;
  const isAdobeProject = typeof project?.adobeAssetId === 'string';
  const isDirectEsmUpload =
    useFeatureFlagEnabled('v4.direct_to_esm') && isAdobeProject;

  // Fail still-parked direct-to-ESM files if their upload URLs never arrive
  // over the subscription, so the job can't hang in progress. Resets per delivery.
  const esmWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const armEsmUploadWatchdog = useCallback(() => {
    if (esmWatchdogRef.current) clearTimeout(esmWatchdogRef.current);
    esmWatchdogRef.current = setTimeout(async () => {
      esmWatchdogRef.current = null;
      const failedCount = await uploader.worker.failPendingEsmUploads();
      if (failedCount > 0) {
        openTransferErrorModal({
          title: TEXT.esmTreeUploadTimeoutTitle,
          details: TEXT.esmTreeUploadTimeoutDetails,
        });
      }
    }, ESM_UPLOAD_URL_TIMEOUT_MS);
  }, [uploader, openTransferErrorModal]);

  //Deliver upload URLs to parked direct-to-ESM files as soon as the backend
  //publishes them. Subscribed on mount so early events aren't missed.
  useGetTransferBatchEventSubscription({
    variables: {
      scope: { updateTransferBatch: { projectId: projectId ?? '' } },
      // Required by the schema; only consumed by the UpdateTransferBatchPayload
      // arm, which this handler ignores.
      assetPagination: { first: 1 },
    },
    skip: !projectId || !isDirectEsmUpload,
    onData: ({ data }) => {
      const event = data.data?.transferBatch;
      if (event?.__typename !== 'AddAssetTreeToTransferBatchPayload') return;

      let deliveredAny = false;
      event.assetWithPaths?.forEach(({ asset }) => {
        const urls = (asset.uploadUrls ?? []).filter(
          (url): url is string => !!url
        );
        if (urls.length === 0) return;
        const totalPartCount = asset.totalPartCount ?? urls.length;
        uploader.worker.receiveEsmTreeUploadUrls(
          asset.id,
          urls,
          totalPartCount
        );
        deliveredAny = true;
      });

      // Reset the idle watchdog on real progress so a slow-but-advancing deep
      // tree is not failed prematurely.
      if (deliveredAny) armEsmUploadWatchdog();
    },
  });

  const refForPriorityQueue = useRef(
    new PQueue({ concurrency: UploaderConfigs.PRIORITY_QUEUE_SIZE.value })
  );
  const refForFileReaders = useRef<Map<string, FileSystemStreamReader>>(
    new Map()
  );

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textErrorMessage = TEXT.errorMessage;

  const onFilesReceived = useCallback(
    async (
      dataTransfer: Omit<Pick<DataTransfer, 'items' | 'files'>, 'items'> & {
        items: DataTransfer['items'] | null;
      }
    ) => {
      const fsEntries =
        scanFileSystemEntries && dataTransfer?.items
          ? getFsEntries(dataTransfer.items)
          : null;
      const fileEntries = Array.from(dataTransfer?.files);

      if (!hasEntries(fileEntries) && (!fsEntries || !hasEntries(fsEntries))) {
        /**
         * The selection provided did not have any files,
         * or was not supported.
         */
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={textErrorMessage}
              variant="error"
            />
          ),
          { duration: 4000 }
        );
        return null;
      }
      /**
       * Until a batch is created we have a temporary local ID
       * for inserting files to the worker:
       */
      let localUploadingId: Scalars['ID']['output'] | null = null;
      try {
        if (uploadType === 'attachment') {
          const { assetId, commentId, parentId } =
            otherInputs as AttachmentsInput;
          if (!assetId) {
            throw new Error('Missing asset id.');
          }
          const jobPrefix = '📎';
          const { id } = await uploader.worker.createCommentAttachmentsJob(
            assetId,
            projectId,
            {
              commentId,
              // Include parent ID only if we don't have a commentId yet.
              commentParentId: !commentId && parentId ? parentId : undefined,
            },
            jobPrefix
          );
          localUploadingId = id;
        } else {
          if (!project) {
            throw new Error('Missing project.');
          }
          if (!folderId) {
            throw new Error('Missing parent folder id.');
          }
          if (!projectId) {
            throw new Error('Missing project id.');
          }
          if (!ancestorFolders) {
            throw new Error('Missing ancestor folders.');
          }
          if (!sourceFolder) {
            throw new Error('Missing source folder.');
          }
          const projectName = project.name;

          const { id, error } = await uploader.worker.createAssetsJob({
            targetFolder: {
              targetFolderId: folderId,
              targetFolderName: sourceFolder.name,
            },
            targetProject: {
              targetProjectId: projectId,
              targetProjectName: projectName,
            },
            ancestorFolders: ancestorFolders.filter((folder) => !!folder),
          });

          localUploadingId = id;

          if (error?.message === 'MAX_CONCURRENT_BATCH_EXCEEDED') {
            track('upload-limit-enforced', {
              limit_type: 'Job Count',
            });
            // @todo (ASSETS-2387): Dry this code out together into a function that just returns this information conditional.
            emitAnalyticsEvent({
              name: CoreAssetsEventNames.UploadedAssets,
              status: 'max-concurrent-batch-exceeded',
              details: {
                id: localUploadingId,
                upload_type: uploadType,
                project_id: projectId,
                max_concurrent_batch_limit: valueOrDefault(
                  UploaderConfigs.MAX_CONCURRENT_BATCH_LIMIT
                ),
              },
            });

            openTransferErrorModal({
              title: TEXT.concurrentBatchLimitExceededTitle,
              details: TEXT.concurrentBatchLimitExceededDetails(
                valueOrDefault(UploaderConfigs.MAX_CONCURRENT_BATCH_LIMIT)
              ),
            });
            uploader.worker.cancelJob(localUploadingId);
            return null;
          }

          if (error?.message === 'MAX_CONCURRENT_FILE_LIMIT_EXCEEDED') {
            track('upload-limit-enforced', {
              limit_type: 'Queue Item Count',
            });

            // @todo (ASSETS-2387): Dry this code out together into a function that just returns this information conditional.
            emitAnalyticsEvent({
              name: CoreAssetsEventNames.UploadedAssets,
              status: 'max-concurrent-file-limit-exceeded',
              details: {
                id: localUploadingId,
                upload_type: uploadType,
                project_id: projectId,
                max_concurrent_file_limit: valueOrDefault(
                  UploaderConfigs.MAX_CONCURRENT_FILE_LIMIT
                ),
              },
            });

            const maxConcurrentLimit = valueOrDefault(
              UploaderConfigs.MAX_CONCURRENT_FILE_LIMIT
            );
            openTransferErrorModal({
              title: TEXT.concurrentFileLimitExceededTitle,
              details: TEXT.concurrentFileLimitExceededDetails(
                fileEntries.length,
                maxConcurrentLimit
              ),
            });
            uploader.worker.cancelJob(localUploadingId);
            return null;
          }
        }
      } catch (e: any) {
        openTransferErrorModal({
          title: TEXT.errorMessage,
          details: e.message,
        });
        return null;
      }

      const readFilesTask = async () => {
        await new Promise<void>((resolve, reject) => {
          const fileReader = new FileSystemStreamReader({
            callbacks: {
              onProgress: ({ items, type }) => {
                if (type === 'FILE') {
                  uploader.worker.addUploadFileItems(localUploadingId, items);
                } else {
                  uploader.worker.addUploadFolderItems(localUploadingId, items);
                }

                return items;
              },
              onError: (error) => {
                if (error.type === ErrorTypes.MAX_FOLDER_DEPTH_EXCEEDED) {
                  track('upload-limit-enforced', {
                    limit_type: 'Folder Depth',
                  });
                  // @todo (ASSETS-2387): Dry this code out together into a function that just returns this information conditional.
                  emitAnalyticsEvent({
                    name: CoreAssetsEventNames.UploadedAssets,
                    status: 'max-folder-depth-exceeded',
                    details: {
                      id: localUploadingId,
                      upload_type: uploadType,
                      project_id: projectId,
                      max_folder_depth_limit: valueOrDefault(
                        UploaderConfigs.MAX_UPLOAD_FOLDER_DEPTH
                      ),
                    },
                  });

                  openTransferErrorModal({
                    title: TEXT.folderDepthLimitExceededTitle,
                    details: TEXT.folderDepthLimitExceededDetails(
                      valueOrDefault(UploaderConfigs.MAX_UPLOAD_FOLDER_DEPTH)
                    ),
                  });

                  uploader.worker.cancelJob(localUploadingId);

                  resolve();
                }

                if (error.type === ErrorTypes.FOLDER_LIMIT_EXCEEDED) {
                  track('upload-limit-enforced', {
                    item_count: error.data.folderCount,
                    limit_type: 'Folder Count',
                  });
                  // @todo (ASSETS-2387): Dry this code out together into a function that just returns this information conditional.
                  emitAnalyticsEvent({
                    name: CoreAssetsEventNames.UploadedAssets,
                    status: 'max-folders-exceeded',
                    details: {
                      id: localUploadingId,
                      upload_type: uploadType,
                      project_id: projectId,
                      max_folder_count: valueOrDefault(
                        UploaderConfigs.MAX_FOLDER_COUNT_PER_JOB
                      ),
                      folder_count: error.data.folderCount,
                    },
                  });

                  uploader.worker.cancelJob(localUploadingId);
                  openTransferErrorModal({
                    title: TEXT.folderLimitExceededTitle,
                    details: TEXT.folderLimitExceededDetails(
                      valueOrDefault(UploaderConfigs.MAX_FOLDER_COUNT_PER_JOB)
                    ),
                  });

                  resolve();
                }

                if (error.type === ErrorTypes.FILE_LIMIT_EXCEEDED) {
                  track('upload-limit-enforced', {
                    item_count: error.data.fileCount,
                    limit_type: 'Item Count',
                  });
                  // @todo (ASSETS-2387): Dry this code out together into a function that just returns this information conditional.
                  emitAnalyticsEvent({
                    name: CoreAssetsEventNames.UploadedAssets,
                    status: 'max-files-exceeded',
                    details: {
                      id: localUploadingId,
                      upload_type: uploadType,
                      project_id: projectId,
                      max_file_count: valueOrDefault(
                        UploaderConfigs.MAX_FILE_COUNT_PER_JOB
                      ),
                      file_count: error.data.fileCount,
                    },
                  });

                  uploader.worker.cancelJob(localUploadingId);
                  openTransferErrorModal({
                    title: TEXT.fileLimitExceededTitle,
                    details: TEXT.fileLimitExceededDetails(
                      valueOrDefault(UploaderConfigs.MAX_FILE_COUNT_PER_JOB)
                    ),
                  });

                  resolve();
                }

                if (error.type === ErrorTypes.EMPTY_FOLDERS_NOT_SUPPORTED) {
                  addToast(
                    (closeToast, toastCloseLabel) => (
                      <ToastAlert
                        closeLabel={toastCloseLabel}
                        onRequestClose={closeToast}
                        title={TEXT.emptyFoldersNotSupported}
                        variant="error"
                      />
                    ),
                    { duration: 4000 }
                  );

                  resolve();
                }

                reject(error);
              },
              onSuccess: () => {
                uploader.worker.finishAddingUploadItems(localUploadingId);
                resolve();
              },
              onCanceled: resolve as () => void,
            },
          });
          fileReader
            .start(fileEntries, fsEntries, 0)
            // Then finalize `setup` once files and folders are partitioned.
            .then(async (fileAndFolderItems) => {
              if (!fileAndFolderItems) return;

              const { fileItems, folderItems } = fileAndFolderItems;

              const isMaxFolderDepthExceeded = folderItems.some(
                (item) =>
                  item.pathArray.length + (ancestorFoldersDepth ?? 0) >=
                  valueOrDefault(UploaderConfigs.MAX_UPLOAD_FOLDER_DEPTH)
              );

              if (isMaxFolderDepthExceeded) {
                fileReader.error({
                  type: ErrorTypes.MAX_FOLDER_DEPTH_EXCEEDED,
                });
                return;
              }

              if (uploadType === 'asset') {
                const { name, topLevelFolderCount, topLevelFileCount } =
                  generateTransferBatchMetadata(fileItems, folderItems);

                await uploader.worker.createTransferBatch(
                  localUploadingId,
                  {
                    name,
                    topLevelFileCount,
                    topLevelFolderCount,
                    accountId,
                  },
                  {
                    isDirectEsmUpload,
                    debug: getDevFlag('UPLOADER_DEBUG_MODE'),
                  }
                );

                // Direct-to-ESM files come back with empty uploadUrls and are
                // parked until the subscription delivers their URLs; start the
                // idle watchdog so parked files can't hang silently.
                if (isDirectEsmUpload) {
                  armEsmUploadWatchdog();
                }
              }
              // Begin processing of files and adding them to the uploader.
              fileReader.process();

              maybeShowNotificationsOptInToast({ action: 'upload' });
            });

          refForFileReaders.current.set(localUploadingId, fileReader);
        });
        refForFileReaders.current.delete(localUploadingId);
      };

      await refForPriorityQueue.current.add(readFilesTask);
      return localUploadingId;
    },
    [
      ancestorFoldersDepth,
      addToast,
      otherInputs,
      projectId,
      uploader,
      uploadType,
      scanFileSystemEntries,
      openTransferErrorModal,
      accountId,
      ancestorFolders,
      folderId,
      project,
      sourceFolder,
      maybeShowNotificationsOptInToast,
      track,
      isDirectEsmUpload,
      armEsmUploadWatchdog,
      textErrorMessage,
    ]
  );

  useEffect(() => {
    const cancelFileReads = ({ job }: { job: { id: string } }) => {
      refForFileReaders.current.get(job.id)?.cancel();
    };
    const promise = uploader.on('jobCanceled', cancelFileReads);

    return (async () => {
      const callbackId = await promise;
      uploader.off('jobCanceled', callbackId);
    }) as () => void;
  }, [uploader]);

  // Clear the ESM upload-URL watchdog on unmount so it can't fire after the
  // component is gone.
  useEffect(
    () => () => {
      if (esmWatchdogRef.current) clearTimeout(esmWatchdogRef.current);
    },
    []
  );

  return onFilesReceived;
}
